import { Directive, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';

@Directive({
  selector: '[appNumericOnly]',
  standalone: true,
})
export class NumericOnlyDirective {
  @Input() appNumericOnlyMaxLength = 10;

  @Output() invalidInputAttempt = new EventEmitter<HTMLInputElement>();

  private allowedKeys = [
    'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
    'Home', 'End',
  ];

  constructor(private el: ElementRef<HTMLInputElement>) {}

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    if (this.allowedKeys.includes(event.key)) {
      return;
    }

    // Block non-digit characters
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      if (event.key.length === 1) {
        this.invalidInputAttempt.emit(this.el.nativeElement);
      }
      return;
    }

    // Block further typing once max length is reached,
    // unless there's a selection being replaced
    const input = this.el.nativeElement;
    const hasSelection = input.selectionStart !== input.selectionEnd;
    if (!hasSelection && input.value.length >= this.appNumericOnlyMaxLength) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    const digitsOnly = pasted.replace(/\D/g, '');

    if (!digitsOnly) {
      if (pasted) {
        this.invalidInputAttempt.emit(this.el.nativeElement);
      }
      return;
    }

    const input = this.el.nativeElement;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    if (digitsOnly !== pasted) {
      this.invalidInputAttempt.emit(input);
    }
    const newValue = (input.value.slice(0, start) + digitsOnly + input.value.slice(end))
      .slice(0, this.appNumericOnlyMaxLength);

    input.value = newValue;
    input.dispatchEvent(new Event('input', { bubbles: true }));

    const cursorPos = Math.min(start + digitsOnly.length, this.appNumericOnlyMaxLength);
    input.setSelectionRange(cursorPos, cursorPos);
  }

  // Fallback for autofill, mobile IME, etc.
  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = input.value.replace(/\D/g, '').slice(0, this.appNumericOnlyMaxLength);
    if (input.value !== sanitized) {
      if (/\D/.test(input.value)) {
        this.invalidInputAttempt.emit(input);
      }
      input.value = sanitized;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
}